import { ChangeEvent, KeyboardEvent, useEffect, useState } from "react";
import styles from "./TextArea.module.css";
import Button from "./Button";
import AIGeneratorButton from "../ai/AIGeneratorButton";

interface TextAreaProps {
    name: string;
    maxLength: number;
    getInputValue?: (value: string) => void;
    label?: string;
    defaultValue?: string;
    cols?: number;
    rows?: number;
    wrap?: string;
    type?: string;
    placeholder?: string;
    isAIGenerator?: boolean;
    mapData?: any[]; // AI 생성에 사용할 데이터
    processIdx?: number; // 프로세스 인덱스 추가
}

const TextArea = (props: TextAreaProps) => {
    const [content, setContent] = useState<string>(props.defaultValue ?? "");
    const [isAIGenerating, setIsAIGenerating] = useState<boolean>(false);
    const rows = props.rows ? props.rows : 10;

    useEffect(() => {
        props.getInputValue && props.getInputValue(content);
    }, [content]);

    const onInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.currentTarget.value;
        const _content = value.length > props.maxLength ? value.slice(0, props.maxLength) : value;
        setContent(_content);
    };

    const onKeyDown = (e: KeyboardEvent) => {
        // 제목을 입력하는 TextArea의 경우 Enter 로 줄바꿈 되지 않도록 처리
        if (props.type === "title" && e.key === "Enter") {
            e.preventDefault();
        }

        if (content.length === props.maxLength && e.key === "Process") {
            //최대 글자수이며 한글입력받았을 경우 추가 입력되지 않도록 처리
            return;
        }
    };

    return (
        <div className={`${styles.content} ${rows === 1 ? styles.row : ""}`}>
            {(props.label || props.isAIGenerator) && (
                <div className={styles.labelArea}>
                    {props.label && <label htmlFor={props.name}>{props.label}</label>}
                    {props.isAIGenerator && (
                        <AIGeneratorButton
                            label="생성"
                            mapData={props.mapData}
                            maxLength={props.maxLength}
                            processIdx={props.processIdx}
                            onTextGenerated={(text: string, isTyping: boolean, isReset?: boolean) => {
                                if (isReset) {
                                    // 새로운 생성 시작 시 텍스트 초기화
                                    setContent('');
                                } else if (isTyping) {
                                    // 타이핑 중일 때 실시간으로 텍스트 업데이트
                                    setContent(prev => prev + text);
                                } else if (text === '') {
                                    // 타이핑 완료 신호 (빈 문자열)
                                    // 아무것도 하지 않음 (이미 타이핑으로 모든 텍스트가 들어감)
                                } else {
                                    // 에러 등으로 전체 텍스트가 한번에 들어오는 경우
                                    setContent(text);
                                }
                            }}
                            disabled={false}
                        />
                    )}
                </div>
            )}
            {props.type === "title" ? (
                <div className={styles.titleContent}>
                    <textarea
                        name={props.name}
                        className={styles.titleInput}
                        onKeyDown={onKeyDown}
                        onChange={onInputChange}
                        rows={1}
                        value={content}
                        wrap="off"
                    />
                    <div className={styles.titleLimit}>
                        {content.length}/{props.maxLength}
                    </div>
                </div>
            ) : (
                <div className={styles.inputContent}>
                    <textarea
                        name={props.name}
                        onKeyDown={onKeyDown}
                        onChange={onInputChange}
                        className={styles.input}
                        cols={props.cols ? props.cols : 40}
                        rows={rows}
                        value={content}
                        placeholder={props.placeholder}
                    />
                    <div className={`${styles.limit}`}>
                        {content.length}/{props.maxLength}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TextArea;
